import { useId, useRef, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { NumberInput } from '../ui/NumberInput';
import { btnGhost, btnPrimary, inputCls } from '../ui/styles';
import { useSubmit } from '../../hooks/useSubmit';
import { addStockItem } from '../adminApi';
import { newRequestId } from '../../core/api/http';
import { toCents } from '../../core/format/currency';
import type { StockItem } from '../../core/types/stock';
type Props = {
  onDone: (item: StockItem) => void;
  onCancel: () => void;
};

/** Een verbruiksartikel (professionele verpakking): alleen voorraad en inkoopprijs, nooit in de webshop of de kassa. */
export function AddConsumableForm({
  onDone,
  onCancel
}: Props) {
  const id = useId();
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [cost, setCost] = useState<number | undefined>(undefined);
  const [touched, setTouched] = useState(false);
  const {
    submit,
    submitting,
    error
  } = useSubmit(addStockItem);
  const request = useRef(newRequestId());
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!name.trim()) return;
    const item = await submit({
      requestId: request.current,
      name: name.trim(),
      ...(brand.trim() ? {
        brand: brand.trim()
      } : {}),
      ...(cost !== undefined ? {
        costPriceCents: toCents(cost)
      } : {})
    });
    if (!item) return;
    toast.success('Verbruiksartikel toegevoegd', {
      description: 'Boek nu de eerste inkoop of telling.'
    });
    onDone(item);
  };
  return <form onSubmit={e => void save(e)} noValidate className="mb-6 space-y-3 rounded-lg border border-line bg-white p-4 sm:p-5" data-id="element-4707" data-mp="3vcu3wzbwowd0">
      <h2 className="text-[15px] font-medium text-ink" data-id="element-4708" data-mp="3avtocv5vlpc">Verbruiksartikel toevoegen</h2>
      <p className="text-[13px] text-muted" data-id="element-4709" data-mp="88nopfr3rnxr">Professionele verpakking voor in de salon. Staat niet in de webshop, de feed of de kassa.</p>
      <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto]" data-id="element-4710" data-mp="2a0kadi6zie69">
        <label htmlFor={`${id}-name`} className="block" data-id="element-4711" data-mp="2a2mz7ywei19e">
          <span className="mb-1 block text-[12px] text-muted" data-id="element-4712" data-mp="3dchj7p0zbyop">Naam</span>
          <input id={`${id}-name`} value={name} onChange={e => setName(e.target.value)} maxLength={120} placeholder="Bijv. Reinigingsmelk 500 ml (salon)" aria-invalid={touched && !name.trim() ? true : undefined} className={inputCls} data-id="element-4713" data-mp="38cwknfpq1sci" />
        </label>
        <label htmlFor={`${id}-brand`} className="block" data-id="element-4714" data-mp="3d601vjvmtg">
          <span className="mb-1 block text-[12px] text-muted" data-id="element-4715" data-mp="3rbr1cu8qx2vb">Merk (optioneel)</span>
          <input id={`${id}-brand`} value={brand} onChange={e => setBrand(e.target.value)} maxLength={60} className={inputCls} data-id="element-4716" data-mp="8wzzflcacdzh" />
        </label>
        <label className="block" data-id="element-4717" data-mp="4w6acon3wt50">
          <span className="mb-1 block text-[12px] text-muted" data-id="element-4718" data-mp="4y0mpekbbcxh">Inkoopprijs (€)</span>
          <NumberInput decimals allowEmpty value={cost} onChange={setCost} className="w-28" data-id="element-4719" data-mp="itbc4cfsf5ug" />
        </label>
      </div>
      {(touched && !name.trim() || error) && <p role="alert" className="text-[12px] text-clay" data-id="element-4720" data-mp="xjfrvkfg8y3u">
          {error ?? 'Geef het artikel een naam.'}
        </p>}
      <div className="flex flex-wrap gap-2" data-id="element-4721" data-mp="12f4truexyr3b">
        <button type="submit" disabled={submitting} aria-busy={submitting} className={btnPrimary} data-id="element-4722" data-mp="nkgjm2ylj4hd">
          {submitting ? 'Toevoegen…' : 'Toevoegen'}
        </button>
        <button type="button" onClick={onCancel} disabled={submitting} className={btnGhost} data-id="element-4723" data-mp="skumz7p95q92">
          Annuleren
        </button>
      </div>
    </form>;
}